import { CircularProgress } from '@shared/components/plugin-components';
import { IntegrationType, RatingMethod } from '@shared/generated/graphql';
import { assertUnreachable } from 'clerk_common/enum';
import { useMarketConditionsContext } from './contexts/MarketConditionsContext';
import { MarketConditions } from './MarketConditions';

export const ratingMethodToIntegrationType = (
  ratingMethod: RatingMethod
): IntegrationType | null => {
  switch (ratingMethod) {
    case RatingMethod.DAT:
      return IntegrationType.DAT;
    case RatingMethod.GREENSCREENS:
      return IntegrationType.GREENSCREENS;
    case RatingMethod.SUNSET:
      return IntegrationType.SUNSET;
    case RatingMethod.TABI:
      return IntegrationType.TABI;
    case RatingMethod.WERNER:
      return IntegrationType.WERNER;
    case RatingMethod.ARCBEST_TL_QUOTING:
      return IntegrationType.ARCBEST_TL_QUOTING;
    case RatingMethod.ECHO:
      return IntegrationType.ECHO;
    case RatingMethod.TRANSFIX:
      return IntegrationType.TRANSFIX_RATING;
    case RatingMethod.BITFREIGHTER:
      return IntegrationType.BITFREIGHTER_RATING;
    case RatingMethod.WWEX:
      return IntegrationType.WWEX;
    case RatingMethod.FASTMORE:
      return IntegrationType.FASTMORE;
    case RatingMethod.NFI:
      return IntegrationType.NFI;
    case RatingMethod.NTG:
      return IntegrationType.NTG;
    case RatingMethod.WORLDWIDE_LOGISTICS:
      return IntegrationType.WORLDWIDE_LOGISTICS;
    case RatingMethod.SONAR:
      return IntegrationType.SONAR;
    case RatingMethod.SOFTMODAL:
      return IntegrationType.SOFTMODAL;
    case RatingMethod.DRAYRATES_RATING:
      return IntegrationType.DRAYRATES_RATING;
    case RatingMethod.HUMAN_INPUT:
    case RatingMethod.DIRECT_OFFER:
    case RatingMethod.CUSTOM:
    case RatingMethod.AVRL:
    case RatingMethod.FREIGHTPLUS:
    case RatingMethod.TRUCKSTOP:
    case RatingMethod.DTS_RATING:
      return null;
    default:
      return assertUnreachable(ratingMethod);
  }
};

export const integrationTypeToRatingMethod = (
  integrationType: IntegrationType
): RatingMethod | null => {
  switch (integrationType) {
    case IntegrationType.AVRL:
      return RatingMethod.AVRL;
    case IntegrationType.DAT:
      return RatingMethod.DAT;
    case IntegrationType.DRAYRATES_RATING:
      return RatingMethod.DRAYRATES_RATING;
    case IntegrationType.DTS_RATING:
      return RatingMethod.DTS_RATING;
    case IntegrationType.GREENSCREENS:
      return RatingMethod.GREENSCREENS;
    case IntegrationType.SUNSET:
      return RatingMethod.SUNSET;
    case IntegrationType.TABI:
      return RatingMethod.TABI;
    case IntegrationType.WERNER:
      return RatingMethod.WERNER;
    case IntegrationType.ARCBEST_TL_QUOTING:
      return RatingMethod.ARCBEST_TL_QUOTING;
    case IntegrationType.ECHO:
      return RatingMethod.ECHO;
    case IntegrationType.TRANSFIX_RATING:
      return RatingMethod.TRANSFIX;
    case IntegrationType.BITFREIGHTER_RATING:
      return RatingMethod.BITFREIGHTER;
    case IntegrationType.WWEX:
      return RatingMethod.WWEX;
    case IntegrationType.FASTMORE:
      return RatingMethod.FASTMORE;
    case IntegrationType.NFI:
      return RatingMethod.NFI;
    case IntegrationType.NTG:
      return RatingMethod.NTG;
    case IntegrationType.WORLDWIDE_LOGISTICS:
      return RatingMethod.WORLDWIDE_LOGISTICS;
    case IntegrationType.SONAR:
      return RatingMethod.SONAR;
    case IntegrationType.SOFTMODAL:
      return RatingMethod.SOFTMODAL;
    case IntegrationType.TRUCKSTOP:
      return RatingMethod.TRUCKSTOP;
    case IntegrationType.ALJEX:
    case IntegrationType.DFM:
    case IntegrationType.EVANS:
    case IntegrationType.GENLOGS:
    case IntegrationType.HIGHWAY:
    case IntegrationType.HOSTED_MCLEOD:
    case IntegrationType.LANDSTAR:
    case IntegrationType.LEGACY_MERCURYGATE:
    case IntegrationType.LOADSMART:
    case IntegrationType.LOADSMART_ALICE:
    case IntegrationType.LOADSTOP:
    case IntegrationType.MACROPOINT:
    case IntegrationType.MCLEOD:
    case IntegrationType.MERCURYGATE:
    case IntegrationType.MICROSOFT_TEAMS:
    case IntegrationType.MICROSOFT:
    case IntegrationType.MODE:
    case IntegrationType.MONTWAY:
    case IntegrationType.MYCARRIERPORTAL:
    case IntegrationType.ORDERFUL:
    case IntegrationType.PROJECT_44_EU:
    case IntegrationType.PROJECT_44:
    case IntegrationType.STEDI:
    case IntegrationType.STRATEGY_LIVE:
    case IntegrationType.TAI:
    case IntegrationType.TEST:
    case IntegrationType.THREE_PL:
    case IntegrationType.TURVO:
    case IntegrationType.UBER_GTG:
    case IntegrationType.VOOMA_PUBLIC_API:
    case IntegrationType.FREIGHTPLUS:
    case IntegrationType.REVENOVA:
    case IntegrationType.SAMSARA:
    case IntegrationType.SLACK:
    case IntegrationType.FRONT:
    case IntegrationType.GOOGLE_WORKSPACE:
    case IntegrationType.INTEGRATIONLESS:
    case IntegrationType.TRUCKER_TOOLS:
    case IntegrationType.MOTIVE:
    case IntegrationType.SAMSARA_LINK_SCRAPING:
    case IntegrationType.MACROPOINT_LINK_SCRAPING:
    case IntegrationType.DHL_FREIGHT:
    case IntegrationType.TRANSFIX:
    case IntegrationType.ALVYS:
      return null;
    default:
      return assertUnreachable(integrationType);
  }
};

interface MarketConditionsContainerProps {
  integrationType: IntegrationType | null;
}

export const MarketConditionsContainer = (
  p: MarketConditionsContainerProps
) => {
  const { marketConditions, loading, forceFetchMarketConditionsMutation } =
    useMarketConditionsContext();

  const marketConditionsForIntegration = marketConditions?.filter(
    (mc) =>
      mc.integrationType === p.integrationType && (mc.stop || mc.errorMessage)
  );

  if (!loading && !marketConditionsForIntegration?.length) return null;

  return (
    <div>
      {loading ? (
        <div className="flex items-center justify-center border-t p-4">
          <CircularProgress size="sm" />
        </div>
      ) : (
        <MarketConditions
          handleRefresh={forceFetchMarketConditionsMutation}
          marketConditions={marketConditionsForIntegration || []}
        />
      )}
    </div>
  );
};
